/**
 * PowerInfoSection - Manage multiple powers on an approved large bond
 * Each power has a power number and execution amount.
 * Powers are saved/deleted via API immediately (not through the bond save).
 */

import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { OathInput, OathButton, OathAlert } from '../../../shared/OathUI';
import { TrashIcon, ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { addPowerInfo, deletePowerFromBond, uploadPowerProof } from '../../../../services/oathLargeBondService';
import CurrencyInput from '../shared/CurrencyInput';

const PowerInfoSection = ({ powers = [], dispatch, bondId, isApproved, disabled }) => {
  const canAddPower = isApproved && bondId;

  // New power form state (not in reducer — local until saved to API)
  const [newPowerNumber, setNewPowerNumber] = useState('');
  const [newExecutionAmount, setNewExecutionAmount] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // File upload state per power
  const [uploadPowerId, setUploadPowerId] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleAddPower = async () => {
    if (!newPowerNumber.trim()) {
      toast.error('Power Number is required');
      return;
    }
    setIsSaving(true);
    const result = await addPowerInfo(bondId, {
      powerNumber: newPowerNumber.trim(),
      executionAmount: newExecutionAmount ? parseFloat(String(newExecutionAmount).replace(/[^\d.]/g, '')) || 0 : 0,
    });

    if (result.success && result.power) {
      dispatch({ type: 'ADD_POWER', power: result.power });
      setNewPowerNumber('');
      setNewExecutionAmount('');
      toast.success('Power added');
    } else {
      toast.error(result.error || 'Failed to add power');
    }
    setIsSaving(false);
  };

  const handleDeletePower = async (power) => {
    setDeletingId(power.largeBondCourtInfoId);
    const result = await deletePowerFromBond(bondId, power.largeBondCourtInfoId);
    if (result.success) {
      dispatch({ type: 'REMOVE_POWER', powerId: power.largeBondCourtInfoId });
      toast.success('Power removed');
    } else {
      toast.error(result.error || 'Failed to remove power');
    }
    setDeletingId(null);
  };

  const handleUpload = async (powerNumber) => {
    if (!files.length || !bondId) return;
    setUploading(true);
    setUploadResult(null);

    const result = await uploadPowerProof(bondId, files, powerNumber);
    setUploadResult({
      success: result.success,
      message: result.success
        ? result.message || `${files.length} file(s) uploaded successfully`
        : result.error || 'Upload failed',
    });

    if (result.success) {
      setFiles([]);
      setUploadPowerId(null);
    }
    setUploading(false);
  };

  const formatCurrency = (val) => {
    const num = Number(val);
    if (isNaN(num) || num === 0) return '-';
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  return (
    <div>
      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Powers</h4>

      {disabled && !isApproved && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Powers can only be added after a bond is approved.
        </p>
      )}

      {/* Existing powers list */}
      {powers.length > 0 && (
        <div className="space-y-3 mb-4">
          {powers.map((power, i) => (
            <div
              key={power.largeBondCourtInfoId}
              className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-center gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Power #{i + 1}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{power.powerNumber || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Execution Amount</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(power.executionAmount)}</p>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {canAddPower && (
                    <>
                      <button
                        onClick={() => {
                          setUploadPowerId(power.largeBondCourtInfoId === uploadPowerId ? null : power.largeBondCourtInfoId);
                          setFiles([]);
                          setUploadResult(null);
                        }}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        title="Upload documents"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePower(power)}
                        disabled={deletingId === power.largeBondCourtInfoId}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove power"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* Inline file upload for this power */}
              {uploadPowerId === power.largeBondCourtInfoId && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex flex-col sm:flex-row items-start gap-2">
                    <OathButton variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      Select Files
                    </OathButton>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      className="hidden"
                      onChange={(e) => setFiles(Array.from(e.target.files))}
                    />
                    {files.length > 0 && (
                      <OathButton size="sm" onClick={() => handleUpload(power.powerNumber)} loading={uploading}>
                        Upload {files.length} File{files.length > 1 ? 's' : ''}
                      </OathButton>
                    )}
                  </div>
                  {files.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {files.map((f, fi) => <p key={fi}>{f.name}</p>)}
                    </div>
                  )}
                  {uploadResult && (
                    <OathAlert
                      variant={uploadResult.success ? 'success' : 'error'}
                      message={uploadResult.message}
                      className="mt-2"
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new power form */}
      {canAddPower && (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add Power</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <OathInput
              label="Power Number"
              value={newPowerNumber}
              onChange={setNewPowerNumber}
              placeholder="Power #"
            />
            <CurrencyInput
              label="Execution Amount"
              value={newExecutionAmount}
              onChange={setNewExecutionAmount}
            />
          </div>
          <OathButton
            size="sm"
            icon={PlusIcon}
            onClick={handleAddPower}
            loading={isSaving}
            disabled={!newPowerNumber.trim()}
            className="mt-3"
          >
            Add Power
          </OathButton>
        </div>
      )}
    </div>
  );
};

export default PowerInfoSection;
